import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import check from "../images/check.png";
import { decryptData } from "../Services/TabServices";
import { Toast } from "../Marketing/Common/Toast";

interface DoctorAppointmentSlotsProps {
  doctor: any;
  onClose: any;
}

const DoctorSlotAvailability: React.FC<DoctorAppointmentSlotsProps> = ({
  doctor,
  onClose,
}) => {
  const doctorId = typeof doctor === "string" ? Number(doctor) : doctor;
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const storedUserId = sessionStorage.getItem("userId");
  const userId = storedUserId ? decryptData(storedUserId) : null;

  const [selectedOption, setSelectedOption] = useState<any>("video");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [nextSevenDays, setNextSevenDays] = useState<
    { date: string; day: string }[]
  >([]);
  const [doctorAvailability, setDoctorAvailability] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [slotError, setSlotError] = useState<string>("");
  const [slot, setSlots] = useState<any>([]);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState<"A" | "D">("A");

  const fetchDoctorAvailability = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/Patient/GetDoctorAvailability`
      );
      const data = response.data;
      const foundDoctor = data.find((doc: any) => doc.doctorId === doctorId);
      setDoctorAvailability(foundDoctor);
    } catch (err) {
      console.error("Error fetching doctor availability:", err);
    }
  };

  useEffect(() => {
    fetchDoctorAvailability();
  }, [doctorId]);

  useEffect(() => {
    const getNextSevenDays = (): { date: string; day: string }[] => {
      const daysArray: { date: string; day: string }[] = [];
      const options: Intl.DateTimeFormatOptions = { weekday: "long" };
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + i);
        daysArray.push({
          date: currentDate.toISOString().split("T")[0],
          day: new Intl.DateTimeFormat("en-US", options)
            .format(currentDate)
            .slice(0, 3),
        });
      }
      return daysArray;
    };
    const days = getNextSevenDays();
    setNextSevenDays(days);
    const firstAvailableDay = days.find((day) => isDayAvailable(day.day));
    if (firstAvailableDay) {
      setSelectedDate(firstAvailableDay.date);
    }
  }, [doctorAvailability]);

  const isDayAvailable = (day: string) => {
    if (!doctorAvailability) return false;
    const dayMapping: { [key: string]: string } = {
      Mon: "monday",
      Tue: "tuesday",
      Wed: "wednesday",
      Thu: "thursday",
      Fri: "friday",
      Sat: "saturday",
      Sun: "sunday",
    };
    return doctorAvailability[dayMapping[day]] === "Available";
  };

  const getDoctorSlots = async () => {
    if (!selectedDate) return;
    setSlots([]);
    setError("");
    setSlotError("");
    setSelectedSlots([]);
    try {
      const res: any = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/Patient/GetDoctorSlotStatus`,
        {
          params: { doctorId, date: selectedDate },
        }
      );
      const slots = res?.data || [];
      setSlots(slots);
    } catch (err: any) {
      console.error("GetDoctorSlot API error:", err);
      if (err.status === 404) {
        setSlotError(err?.response?.data || "No slots found");
      }
      setSlots([]);
    }
  };

  // Reset slots and refetch when actionType or date changes
  useEffect(() => {
    setSelectedSlots([]);
    setError("");
    if (selectedDate) {
      getDoctorSlots();
    }
  }, [actionType, selectedDate]);

  const generateTimeSlots = (
    startTime: string,
    endTime: string,
    interval: number
  ) => {
    const slots = [];
    let current = new Date();
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    current.setHours(startHour, startMin, 0, 0);
    const endTimeObj = new Date();
    endTimeObj.setHours(endHour, endMin, 0, 0);
    while (current <= endTimeObj) {
      const timeSlot = current.toTimeString().slice(0, 5);
      slots.push(timeSlot);
      current.setMinutes(current.getMinutes() + interval);
    }
    return slots;
  };

  const dummySlots = generateTimeSlots("13:00", "16:30", 30);

  const isSlotInFuture = (slotStartTime: string, date: string): boolean => {
    const now = new Date();
    const slotDateTime = new Date(`${date}T${slotStartTime}`);
    return slotDateTime.getTime() - now.getTime() >= 10 * 60 * 1000;
  };

  const getSelectableSlots = () => {
    return slot.filter((slotTime: any) => {
      const isToday =
        selectedDate === new Date().toISOString().split("T")[0];
      const isFutureSlot =
        !isToday || isSlotInFuture(slotTime.slotStartTime, selectedDate);

      if (actionType === "A") {
        return (
          slotTime.status === "Unavailable" &&
          isFutureSlot &&
          slotTime.status !== "Booked"
        );
      } else {
        return slotTime.status === "Available" && isFutureSlot;
      }
    });
  };

  const hasSelectableSlots = getSelectableSlots().length > 0;

  const handleSlotSelection = (slotId: number) => {
    setError("");
    setSelectedSlots((prev) => {
      if (prev.includes(slotId)) {
        return prev.filter((id) => id !== slotId);
      } else {
        return [...prev, slotId];
      }
    });
  };

  const handleSelectAll = () => {
    const selectableSlots = getSelectableSlots();
    const selectableSlotIds = selectableSlots.map((slot: any) => slot.slotId);

    if (selectedSlots.length === selectableSlotIds.length) {
      setSelectedSlots([]);
    } else {
      setSelectedSlots(selectableSlotIds);
    }
  };

  const handleProceed = async () => {
    if (selectedSlots.length === 0 || !selectedDate || !doctorId) {
      setError("Please select at least one slot.");
      return;
    }

    setIsProcessing(true);
    setError("");

    try {
      const payload = {
        DoctorId: doctorId,
        userId: userId ? parseInt(userId, 10) : null,
        SlotDate: selectedDate,
        Slots: selectedSlots.map((slotId) => ({ SlotId: slotId })),
        Action: actionType,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Patient/ActivateAndDeactivateSlots`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const message = response.data.message;
      setShowConfirmationPopup(true);
      setTimeout(() => {
        setShowConfirmationPopup(false);
  onClose()

      }, 3000);
      Toast.success(message);
      setSelectedSlots([]);
      getDoctorSlots();
    } catch (err: any) {
      console.error("Error creating slot:", err);
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to update slot availability. Please try again.";
      setError(errorMessage);
      Toast.error(errorMessage);

      setTimeout(() => {
        setError("");
      }, 3000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-white bg-opacity-10 flex justify-center items-center z-50"
      onClick={onClose}
    >
      {showConfirmationPopup && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="flex justify-center items-center p-10">
            <div className="bg-white p-10 rounded-lg shadow-lg">
              <div className="flex justify-center mb-2">
                <img src={check} alt="Success" className="w-6 h-6" />
              </div>
              <p className="font-semibold text-lg text-center">
                Slot availability has been updated!
              </p>
              <p className="text-center text-xs text-[#606060] mt-1">
                Thank you for updating slots
              </p>
            </div>
          </div>
        </div>
      )}

      <div
        className="bg-white rounded-lg shadow-lg w-[390px] xs:w-[400px] sm:w-[400px] md:w-[450px] lg:w-[500px] xl:w-[550px] relative min-h-[60vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center bg-white">
          <div className="sticky top-[184px] px-1 xs:px-4 py-8">
            <div className="flex justify-center">
              <p className="text-sm font-semibold">
                Doctor Slots Availability
              </p>
            </div>

           

            <div className="flex items-center gap-2 xs:gap-4 text-sm pt-4">
              <p
                role="button"
                aria-selected={selectedOption === "video"}
                onClick={() => setSelectedOption("video")}
                className={`px-2 xs:px-4 sm:px-2 py-2 rounded-lg cursor-pointer transition-all duration-300
                  ${
                    selectedOption === "video"
                      ? "bg-[#009A8526] text-[#009A85] font-semibold shadow-md"
                      : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                  }`}
              >
                Video Consultation
              </p>
              <p
                role="button"
                aria-selected={selectedOption === "hospital"}
                className={`px-2 xs:px-4 py-2 rounded-lg cursor-pointer transition-all duration-300
                  ${
                    selectedOption === "hospital"
                      ? "bg-[#009A8526] text-green-600 font-semibold shadow-md"
                      : "bg-gray-300 text-gray-700"
                  }`}
              >
                Hospital Visit
              </p>
             
            </div>
 {/* Action Type Radio Buttons - FIXED both colors */}
            <div className="flex items-center justify-center gap-6 mt-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="actionType"
                  value="A"
                  checked={actionType === "A"}
                  onChange={() => setActionType("A")}
                  className="w-4 h-4 accent-[#009A85]"
                />
                <span className="text-sm font-medium text-[#009A85]">Activate</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="actionType"
                  value="D"
                  checked={actionType === "D"}
                  onChange={() => setActionType("D")}
                  className="w-4 h-4 accent-[#009A85]"
                />
                <span className="text-sm font-medium  text-[#009A85]">Deactivate</span>
              </label>
            </div>
            <div>
              <p className="text-[14px] pt-4 pb-2">Choose Date</p>
              <div className="flex gap-2 xs:gap-2 sm:gap-4">
                {nextSevenDays.map((day, index) => {
                  const isAvailable = isDayAvailable(day.day);
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (isAvailable) {
                          setSelectedDate(day.date);
                          setError("");
                        }
                      }}
                      style={{
                        backgroundColor:
                          selectedDate === day.date ? "#009A8533" : "white",
                        opacity: isAvailable ? 1 : 0.5,
                        pointerEvents: isAvailable ? "auto" : "none",
                      }}
                      className="w-[45px] h-[55px] border border-[#009A85] rounded-[5px] cursor-pointer"
                    >
                      <div className="flex flex-col items-center">
                        <p className="text-[#009A85] text-[14px]">
                          {day.date.slice(8, 10)}
                        </p>
                        <p className="text-[#009A85] text-[14px]">{day.day}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center pt-4 pb-2">
                <p className="text-[14px]">Choose Time</p>
                {hasSelectableSlots && (
                  <button
                    onClick={handleSelectAll}
                    className="text-[12px] text-[#009A85] font-semibold hover:underline"
                  >
                    {selectedSlots.length === getSelectableSlots().length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                )}
              </div>

              {/* No slots message for current action type */}
              {!hasSelectableSlots && selectedDate && slot.length > 0 && (
                <div className="flex justify-center mb-4">
                  <p className="text-[12px] text-gray-500 font-medium">
                    {actionType === "A"
                      ? "No slots available for activation on this date"
                      : "No slots available for deactivation on this date"}
                  </p>
                </div>
              )}

              {slot.length > 0 ? (
                <div className="grid grid-cols-5 xs:grid-cols-5 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-6 gap-3">
                  {slot.map((slotTime: any, index: any) => {
                    const isToday =
                      selectedDate === new Date().toISOString().split("T")[0];
                    const isFutureSlot =
                      !isToday ||
                      isSlotInFuture(slotTime.slotStartTime, selectedDate);
                    const isBooked = slotTime.status === "Booked";

                    let isSelectable = false;
                    let displaySlot = false;

                    if (actionType === "A") {
                      displaySlot =
                        slotTime.status === "Unavailable" && isFutureSlot;
                      isSelectable = displaySlot && !isBooked;
                    } else {
                      displaySlot =
                        slotTime.status === "Available" && isFutureSlot;
                      isSelectable = displaySlot;
                    }

                    const isSelected = selectedSlots.includes(slotTime.slotId);

                    if (!displaySlot && !isBooked) return null;

              
                    let baseBorderColor = "#ccc";
                    let baseTextColor = "#999";

                    if (isBooked) {
                      baseBorderColor = "#ccc";
                      baseTextColor = "#999";
                    } else if (slotTime.status === "Available") {
                      baseBorderColor = "#184A84";
                      baseTextColor = "#184A84";
                    } else if (slotTime.status === "Unavailable") {
                      baseBorderColor = "#fa8a01ff";
                      baseTextColor = "#fa8a01ff";
                    }

                   
                    let borderColor = baseBorderColor;
                    let textColor = baseTextColor;
                    let backgroundColor = "white";

                    if (isSelected && !isBooked) {
                      if (actionType === "A") {
                        borderColor = "#184A84";
                        textColor = "#184A84";
                        backgroundColor = "#fcfcfcfb";
                      } else {
                        borderColor = "#fa8a01ff";
                        textColor = "#fa8a01ff";
                        backgroundColor = "#fa8a0126";
                      }
                    }

                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (isSelectable) {
                            handleSlotSelection(slotTime.slotId);
                          }
                        }}
                        style={{
                          opacity: isSelectable || isBooked ? 1 : 0.3,
                          borderColor: borderColor,
                          backgroundColor: backgroundColor,
                          color: textColor,
                          pointerEvents: isSelectable ? "auto" : "none",
                        }}
                        className="flex justify-center items-center w-[55px] h-[35px] border-2 rounded-[5px] cursor-pointer transition-all"
                      >
                        <p className="text-[12px]">
                          {slotTime.slotStartTime.slice(0, 5)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : !selectedDate || slotError ? (
                <>
                  <div className="grid grid-cols-5 xs:grid-cols-5 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-6 gap-3">
                    {dummySlots.map((slotTime, index) => (
                      <div
                        key={`dummy-${index}`}
                        style={{
                          backgroundColor: "white",
                          opacity: 0.9,
                          borderColor: "#ccc",
                          color: "gray",
                          pointerEvents: "none",
                        }}
                        className="flex justify-center items-center w-[55px] h-[35px] border rounded-[5px] cursor-not-allowed"
                      >
                        <p>{slotTime}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>

            {error ? (
              <p className="flex justify-center text-red-600 text-[10px] mt-2">
                {error}
              </p>
            ) : (
              <p className="text-[12px] mt-5">
                {actionType === "A"
                  ? "Select deactivated slots to activate"
                  : "Select available slots to deactivate"}
              </p>
            )}

            {selectedSlots.length > 0 && (
              <div className="flex justify-center mt-3">
                <button
                  onClick={handleProceed}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-md text-white ${
                    actionType === "A"
                      ? "bg-[#009A85] hover:bg-[#007F72]"
                      : "bg-[#fa8a01ff] hover:bg-[#e07a00]"
                  } transition 
                  ${
                    isProcessing
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  {isProcessing
                    ? "Processing..."
                    : `${actionType === "A" ? "Activate" : "Deactivate"} ${
                        selectedSlots.length
                      } Slot${selectedSlots.length > 1 ? "s" : ""}`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorSlotAvailability;
